import React, { useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { useTypewriter } from '../hooks/useTypewriter';
const typewriterWords = ['eine schöne Körperhaltung haben!', 'Spagat lernen.', 'Brücke lernen.', 'Rückenschmerzen loswerden.'];
export function Hero() {
  const typed = useTypewriter(typewriterWords);
  const bgRef = useRef<HTMLImageElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollIndicatorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      rafId = requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const vh = window.innerHeight;
        // Parallax: bg image moves at 40% of scroll speed (slower = depth illusion)
        if (bgRef.current) {
          bgRef.current.style.transform = `translateY(${scrollY * 0.4}px) scale(1.1)`;
        }
        // Content drifts up and fades out as user scrolls
        if (contentRef.current) {
          const progress = Math.min(scrollY / (vh * 0.6), 1);
          contentRef.current.style.opacity = String(Math.max(1 - progress * 1.3, 0));
          contentRef.current.style.transform = `translateY(${scrollY * 0.18}px)`;
        }
        // Overlay gradually darkens
        if (overlayRef.current) {
          const extra = Math.min(scrollY / vh, 0.35);
          overlayRef.current.style.background = `rgba(26,10,30,${extra})`;
        }
        // Scroll indicator fades out quickly
        if (scrollIndicatorRef.current) {
          const fade = Math.max(1 - scrollY / 150, 0);
          scrollIndicatorRef.current.style.opacity = String(fade);
        }
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);
  return <section className="relative h-screen w-full overflow-hidden flex items-center justify-center" data-id="element-113">
      {/* Parallax Background */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-114">
        <img ref={bgRef} src="https://cdn.magicpatterns.com/uploads/krYbADmCsZf3d57xVfG4iN/IMG_6051.JPG.webp" alt="Flex Club Studio" className="w-full h-full object-cover object-center scale-110 will-change-transform" style={{
        transformOrigin: 'center top'
      }} data-id="element-115" />
        {/* Static dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-studio/70 via-dark-studio/50 to-dark-studio/80" data-id="element-116"></div>
        {/* Dynamic scroll-driven overlay */}
        <div ref={overlayRef} className="absolute inset-0 will-change-[background]" data-id="element-117"></div>
        {/* Peony tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-peony-900/30 via-transparent to-dark-deep/40" data-id="element-118"></div>
        {/* Animated blobs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-peony-600/10 rounded-full mix-blend-screen filter blur-3xl animate-blob" data-id="element-119"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-neon-pink/10 rounded-full mix-blend-screen filter blur-3xl animate-blob animation-delay-2000" data-id="element-120"></div>
      </div>

      {/* Content – parallax drift + fade */}
      <div ref={contentRef} className="relative z-10 text-center px-4 max-w-5xl mx-auto will-change-transform" data-id="element-121">
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight leading-tight animate-fade-in-up text-shadow-lg" data-id="element-122">
          Mach Bewegung zu <br data-id="element-123" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-peony-300 to-white" data-id="element-124">
            deinem Lifestyle
          </span>
        </h1>

        {/* Typewriter line */}
        <div className="mb-8 animate-fade-in-up min-h-[2.5rem] md:min-h-[3rem]" style={{
        animationDelay: '0.15s'
      }} data-id="element-125">
          <p className="text-2xl md:text-3xl font-semibold text-white/90 text-shadow overflow-hidden" data-id="element-126">
            Du willst&nbsp;
            <span className="text-peony-300" data-id="element-127">
              <span className="inline" data-id="element-128">{typed}</span>
              <span className="inline-block w-[2px] h-7 md:h-8 bg-peony-300 ml-1 align-middle animate-pulse rounded-full" data-id="element-129" />
            </span>
          </p>
        </div>

        <p className="text-xl md:text-2xl text-peony-100 mb-10 max-w-3xl mx-auto font-light animate-fade-in-up" style={{
        animationDelay: '0.2s'
      }} data-id="element-130">
          Stretching, Mobility & Fitness exklusiv für Frauen in Nürnberg
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up" style={{
        animationDelay: '0.4s'
      }} data-id="element-131">
          <a href="#courses" className="btn-primary w-full sm:w-auto text-center" data-id="element-132">
            Probetraining buchen
          </a>
          <a href="#courses" className="btn-outline w-full sm:w-auto text-center" data-id="element-133">
            Trainingskalender ansehen
          </a>
        </div>
      </div>

      {/* Floating decorative elements */}
      <div className="absolute top-1/4 left-10 hidden lg:block animate-float" data-id="element-134">
        <div className="w-16 h-16 rounded-full border-2 border-white/20 backdrop-blur-sm bg-white/5" data-id="element-135"></div>
      </div>
      <div className="absolute bottom-1/4 right-10 hidden lg:block animate-float-delayed" data-id="element-136">
        <div className="w-24 h-24 rounded-full border-2 border-peony-400/30 backdrop-blur-sm bg-peony-500/10" data-id="element-137"></div>
      </div>

      {/* Scroll Indicator – fades on scroll */}
      <div ref={scrollIndicatorRef} className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-white/50 will-change-[opacity]" data-id="element-138">
        <ArrowDown size={32} data-id="element-139" />
      </div>
    </section>;
}